"use client";

/**
 * B-240 step 3 (2026-09-18): the "Cheaper elsewhere" strip. Shown only when
 * a cheaper offer failed one of the winner checks (parallel import, used).
 * Same anatomy as the Price Check strip directly above it: white rounded
 * strip, one pill (the strip's own warn style), one sentence, one text link.
 * The price is bold in the page's savings green, like "saved $X" on the card.
 * "See it" opens the folded list and lands on that shop's card. Never leaves
 * the page.
 */

import type { ProductPageData } from "@/lib/api/types";
import { prettifyRetailerSlug } from "@/lib/retailers/prettify";
import { formatAUD } from "@/lib/utils";

import { SEE_EVENT } from "./RetailerListFold";

export function CheaperElsewhereStrip({ offer, reason }: { offer: ProductPageData; reason: string }) {
  const shop = prettifyRetailerSlug(offer.retailer_slug);
  return (
    <section className="mt-4 rounded-2xl bg-secondary px-6 py-4 shadow-sm" aria-label="Cheaper offer with a catch">
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="pc-pill-warn whitespace-nowrap rounded-full px-3 py-1 text-[13px] font-bold">Cheaper elsewhere</span>
          <span className="text-[14.5px] text-foreground">
            {shop} has it for{" "}
            <b className="font-semibold tabular-nums text-[var(--cmp-good)]">{formatAUD(offer.delivered_total)}</b>.{" "}
            <span className="text-muted-foreground">{reason}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent(SEE_EVENT, { detail: offer.retailer_slug }))}
          className="flex-shrink-0 self-start whitespace-nowrap text-[13px] font-medium text-[var(--cmp-warn)] underline-offset-4 hover:underline sm:self-auto"
        >
          See it →
        </button>
      </div>
    </section>
  );
}
